// The project schematic (Brief 5 / pt7) — a calm structural map of the bound
// project: "the project's shape at a glance", NOT a file browser. The bound repo
// gives it data (the server reduces the GitHub git-tree to ranked AREAS + a few
// landmark files); this renders it as a place, and a read/write op can light up
// a LOCATION on it.
//
// IA: two zones. The AREAS are the primary structure — the code-bearing
// directories, ranked by size, a monorepo split shown one level in (a depth-1
// child indents under its parent). A thin META STRIP underneath pins the
// landmark files (README, the manifest, an entry point) as a footnote — the
// stack at a glance, not a place. Each area carries its stable repo-relative
// PATH, so the indicator folds an op onto the nearest tile.
//
// Calm + a11y: token-pure, lowercase, `font-code` for paths. The lit
// area (where a write/read points) is marked by colour AND a left ring AND
// `aria-current="location"` — never colour alone, and visible without motion.
// `role="tree"` over the areas so the structure is navigable. Honest about a
// truncated map ("showing N of M areas") so it never pretends to be whole.

import type { SchematicResponse } from "@waytide/core";
import type { SchematicState } from "../hooks/useSchematic";
import "../styles/projectSchematic.css";

/** The areas zone — the code-bearing directories as the project's structure. A
 * depth-1 area (a monorepo-split child) indents under its parent via a data attr. */
function Areas({
  schematic,
  litArea,
}: {
  schematic: SchematicResponse;
  litArea: string;
}) {
  if (schematic.areas.length === 0) {
    return (
      <p className="wt-schem__empty">
        no top-level areas — this repo keeps its code at the root.
      </p>
    );
  }
  return (
    <ul className="wt-schem__areas" role="tree" aria-label="project areas">
      {schematic.areas.map((a) => {
        const lit = litArea !== "" && litArea === a.path;
        // A calm child count, only when there's something to say.
        const meta =
          a.depth === 0 && (a.dir_count > 0 || a.file_count > 0)
            ? [
                a.dir_count > 0 ? `${a.dir_count} dirs` : null,
                a.file_count > 0 ? `${a.file_count} files` : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : "";
        return (
          <li
            key={a.path}
            className="wt-schem__area"
            role="treeitem"
            data-depth={a.depth}
            data-lit={lit ? "true" : undefined}
            aria-current={lit ? "location" : undefined}
            title={a.path}
          >
            <span className="wt-schem__area-ring" aria-hidden="true" />
            <span className="wt-schem__area-name font-code">{a.name}</span>
            {meta ? <span className="wt-schem__area-meta">{meta}</span> : null}
            {lit ? <span className="wt-schem__area-here">active</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

/** The meta strip — the landmark files (README, manifest, entry) as a footnote
 * row, distinct in weight from the areas so the map reads as a place + a label,
 * not one long list. */
function Landmarks({ schematic }: { schematic: SchematicResponse }) {
  if (schematic.files.length === 0) return null;
  return (
    <div className="wt-schem__meta" aria-label="landmark files">
      {schematic.files.map((f) => (
        <span key={f.path} className="wt-schem__file font-code" title={f.path}>
          {f.name}
        </span>
      ))}
    </div>
  );
}

/** The schematic panel — header (repo · branch), the areas, the meta strip, and
 * an honest partial note. `litArea` is the area path the current read/write
 * points at (already folded by the caller), or "" when nothing is lit. */
export function ProjectSchematic({
  state,
  litArea = "",
}: {
  state: SchematicState;
  litArea?: string;
}) {
  if (state.kind === "loading") {
    return (
      <aside className="wt-schem" aria-label="project schematic" aria-busy="true">
        <p className="wt-schem__status">reading the project's shape…</p>
      </aside>
    );
  }
  if (state.kind === "unbound") {
    return (
      <aside className="wt-schem" aria-label="project schematic">
        <p className="wt-schem__status">
          bind this session to a repo to see its shape.
        </p>
      </aside>
    );
  }
  if (state.kind === "reconnect") {
    return (
      <aside className="wt-schem" aria-label="project schematic">
        <p className="wt-schem__status">reconnect github to map the project.</p>
      </aside>
    );
  }
  if (state.kind === "error") {
    return (
      <aside className="wt-schem" aria-label="project schematic">
        <p className="wt-schem__status">the project map is unavailable right now.</p>
      </aside>
    );
  }

  const { schematic } = state;
  const shown = schematic.areas.filter((a) => a.depth === 0).length;
  return (
    <aside className="wt-schem" aria-label="project schematic">
      <div className="wt-schem__head">
        <span className="wt-schem__repo font-code">{schematic.full_name}</span>
        <span className="wt-schem__branch font-code">{schematic.branch}</span>
      </div>
      <Areas schematic={schematic} litArea={litArea} />
      <Landmarks schematic={schematic} />
      {schematic.partial ? (
        <p className="wt-schem__partial">
          showing {shown} of {schematic.area_count} areas — the project's shape, not
          every file.
        </p>
      ) : null}
    </aside>
  );
}
